// SPDX-License-Identifier: Elastic-2.0
/**
 * Banner — persistent inline notice.
 *
 * Renders at the top of a page or section. Unlike `Toast`, it does NOT
 * auto-dismiss; visibility is owned by the caller. Pass `dismissable`
 * to surface a close button that fires `onDismiss`.
 *
 * Used by (future tasks):
 *  - Offline-page wrapper (functional spec § 12)
 *  - "WebSocket reconnecting" indicator (T-030)
 *
 * Accessibility: role mirrors Toast — info/success use `role="status"`,
 * warning/error use `role="alert"`. Banner is rendered statically (not
 * inside an `aria-live` region) — the role itself is announced.
 */
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";

import styles from "./Banner.module.css";

export type BannerKind = "info" | "success" | "warning" | "error";

export interface BannerProps {
  kind: BannerKind;
  /** Optional bold title shown above the message. */
  title?: string;
  /** Required descriptive text. */
  message: string;
  /** When true, render a close button that fires `onDismiss`. */
  dismissable?: boolean;
  /** Called when the close button is clicked (only when `dismissable`). */
  onDismiss?: () => void;
  /** Extra class names forwarded to the root element. */
  className?: string;
}

const KIND_CLASS: Record<BannerKind, string> = {
  info: styles.info ?? "",
  success: styles.success ?? "",
  warning: styles.warning ?? "",
  error: styles.error ?? "",
};

function ariaRole(kind: BannerKind): "status" | "alert" {
  return kind === "warning" || kind === "error" ? "alert" : "status";
}

export function Banner({
  kind,
  title,
  message,
  dismissable,
  onDismiss,
  className,
}: BannerProps): ReactElement {
  const { t } = useTranslation();
  const classes = [styles.banner, KIND_CLASS[kind], className].filter(Boolean).join(" ");
  return (
    <div className={classes} role={ariaRole(kind)} data-banner-kind={kind}>
      <div className={styles.body}>
        {title ? <div className={styles.title}>{title}</div> : null}
        <div className={styles.message}>{message}</div>
      </div>
      {dismissable ? (
        <button
          type="button"
          className={styles.close}
          aria-label={t("feedback.dismiss")}
          onClick={onDismiss}
        >
          {"×"}
        </button>
      ) : null}
    </div>
  );
}
